import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { LEGACY_DATASETS_TOOLTIP } from 'webapp/enums/metric-mover';

import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import LineageExplorerHostComponent, {
	type OpenLineageExplorer,
} from 'webapp/components/lineage-explorer-host';
import MetricLibraryMetricActionsBaseComponent from 'webapp/components/metric-library/metric/actions-base';
import NotificationsButtonComponent from 'webapp/components/notifications/button';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { Metric, MetricSetup } from 'webapp/models-custom';
import type DateRange from 'webapp/models-custom/date-range';

import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

interface Args {
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	origin: TrackingOriginForMetricEdit;
	onMetricEdit: (metric: Metric) => void;
	onShare?: () => void;
	onClose?: () => void;
}

type DropdownAction =
	| 'share'
	| 'createBlock'
	| 'manageGoal'
	| 'duplicateMetric'
	| 'editMetric'
	| 'editScript'
	| 'editQuery'
	| 'pushData'
	| 'deleteMetric'
	| 'toggleFavorite'
	| 'viewLineage';

type Option = PopupListItem<DropdownAction> & {
	deprioritize?: boolean;
};

export default class MetricDetailsExploreToolbarComponent extends MetricLibraryMetricActionsBaseComponent<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	@tracked isFavoriteToggleInProgress = false;
	@tracked isAddToHomeInProgress = false;

	private openLineage: OpenLineageExplorer | null = null;

	get metricSetup() {
		return this.args.metricSetup;
	}

	get metric() {
		return this.metricSetup.metric;
	}

	get origin() {
		return this.args.origin;
	}

	get isAddToHomeShown(): boolean {
		return !!this.metric && this.currentUserService.hasHome;
	}

	get isMetricToggleInProgress() {
		return this.isFavoriteToggleInProgress;
	}

	// --- Favorite ---

	get isFavorite() {
		return !!this.metric?.isFavorite;
	}

	@action
	toggleFavorite() {
		if (this.isMetricToggleInProgress) {
			return;
		}

		this.isFavoriteToggleInProgress = true;
		const requestPromise = this.isFavorite ? this.removeFromFavorites() : this.addToFavorites();

		void requestPromise.then(() => {
			this.isFavoriteToggleInProgress = false;
		});
	}

	// --- Home ---

	@action
	addToHome() {
		if (this.isAddToHomeInProgress) {
			return;
		}

		this.isAddToHomeInProgress = true;
		void this.mmMetricsService.addMetricSetupsToHome([this.metricSetup]).finally(() => {
			this.isAddToHomeInProgress = false;
		});
	}

	// --- Create Block ---

	@action
	override createBlock() {
		const dateRangeCopy = this.args.dateRange?.createCopy();
		super.createBlock({
			dimensions: this.metricSetup.dimensions,
			filters: this.metricSetup.filtersData,
			dateRangeFormula: dateRangeCopy?.formula,
		});
	}

	// --- Goals ---

	@action
	manageGoal() {
		this.mmGoalsService.openNewPopupForMetricSetup(this.metricSetup, this.args.origin);
	}

	override afterMetricEdit(metrics: Metric[]) {
		this.args.onMetricEdit(metrics[0]!);
	}

	// --- Options ---

	get options(): Option[] {
		const options: Option[] = [];
		const metric = this.metricSetup.metric;

		if (this.mmMetricsService.showFavoriteAction) {
			options.push({
				icon: this.isFavorite ? 'StarFill' : 'Star',
				text: this.isFavorite ? 'Unfavorite' : 'Favorite',
				action: 'toggleFavorite',
				class: this.isFavorite ? 'accent' : '',
				isLoading: this.isFavoriteToggleInProgress,
			});
		}

		if (!metric) {
			return options;
		}

		if (this.args.onShare) {
			options.push({
				icon: 'Link',
				text: 'Copy URL',
				action: 'share',
			});
		}

		options.push({
			icon: 'Databoards',
			text: 'Add to dashboard',
			action: 'createBlock',
		});

		// TODO: Remove isTypeHorizon guard once lineage is supported for horizon metrics
		if (metric.key && !metric.isTypeHorizon) {
			options.push({
				icon: 'Workflow',
				text: 'Lineage explorer',
				action: 'viewLineage',
			});
		}

		if (this.metricSetup.isSingleOutput) {
			if (!metric.isTypeGoal) {
				options.push({
					icon: 'OkRs',
					text: 'Set your goal...',
					action: 'manageGoal',
					extra: this.billingService.goalsEnabled
						? undefined
						: { type: 'featureLimitation' },
				});
			}
		}

		if (this.showDuplicateMetric) {
			options.push({
				icon: 'Copy',
				text: 'Duplicate metric...',
				action: 'duplicateMetric',
				isDisabled: this.isDuplicateMetricBlocked,
				disabledTooltipText: this.isDuplicateMetricBlocked
					? LEGACY_DATASETS_TOOLTIP
					: undefined,
			});
		}

		if (this.queryModel?.canEdit) {
			options.push({
				icon: 'Pencil',
				text: 'Edit custom metric',
				action: 'editQuery',
				extra: this.billingService.qbEnabled ? undefined : { type: 'featureLimitation' },
			});
		}

		if (this.scriptModel?.canEdit) {
			options.push({
				icon: 'Pencil',
				text: 'Edit calculated metric',
				action: 'editScript',
				extra: this.billingService.scriptsEnabled
					? undefined
					: { type: 'featureLimitation' },
			});
		}

		if (metric?.canEditDefinition) {
			options.push({
				icon: 'Settings',
				text: 'Edit metric definition',
				action: 'editMetric',
			});
		}

		if (this.canPushData) {
			options.push({
				icon: 'Upload',
				text: 'Store values',
				action: 'pushData',
				extra: this.billingService.dataPushEnabled
					? undefined
					: { type: 'featureLimitation' },
			});
		}

		if (this.canDelete) {
			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'deleteMetric',
				icon: 'Trash',
			});
		}

		return options;
	}

	@action
	doAction(actionName?: string, ..._args: unknown[]): void {
		if (!actionName) {
			return;
		}

		if (actionName === 'share') {
			this.args.onShare?.();
		} else {
			this[actionName as Exclude<DropdownAction, 'share'>]();
		}
	}

	@action
	override deleteMetric() {
		this.mmMetricsService.deleteMetric(this.metric, this.args.onClose);
	}

	@action
	captureLineageOpen(open: OpenLineageExplorer) {
		this.openLineage = open;
	}

	@action
	viewLineage() {
		const metricKey = this.metricSetup.lineageMetricKey;
		if (metricKey) {
			this.openLineage?.({ target: { metricKey } });
		}
	}

	<template>
		<div class="dbx-ui-actions metric-details__header-actions">
			{{! The mobile drawer only explores the metric: every action here edits it or leaves the drawer. }}
			{{#unless this.metricDetailsService.isMobileMode}}
				<CommonCollapsiblePillOptionsComponent
					@itemsList={{this.options}}
					@doAction={{this.doAction}}
					@maxVisibleItems={{1}}
				/>

				<LineageExplorerHostComponent @onReady={{this.captureLineageOpen}} />

				<NotificationsButtonComponent
					@model={{this.metricSetup}}
					@origin="metrics_detail"
				/>

				{{#if this.isAddToHomeShown}}
					<SubmitButtonComponent
						@text="Add to Home"
						@icon="Plus"
						@theme="blue"
						@size="normal"
						@onTrigger={{this.addToHome}}
						@isDisabled={{this.isAddToHomeInProgress}}
						@noDelay={{true}}
					/>
				{{/if}}
			{{/unless}}

			{{#if @onClose}}
				<a role="button" class="metric-details__close" {{on "click" @onClose}}>
					<CommonUiIconComponent @name="Close" @size="lg" />
				</a>
			{{/if}}
		</div>
	</template>
}
